/**
 * Wrapped version of fslightbox-react
 * Documentation: https://fslightbox.com/react/documentation
 */
import React, { useEffect, useRef,useState } from "react";
import FsLightbox, { FsLightboxProps } from "fslightbox-react";

type Source = string | React.ReactElement;

export type LightboxProps = {
  // Whether the lightbox should be open. The intended open state.
  open: boolean;
  // Will be called when the lightbox closes, you must use this to update the open state or you may see strange behaviour as the intended open state may be out of sync with the true open state that is managed internally by fslightbox-react
  onClose: () => void;
  // Source may be a youtube or orbitvu link.
  sources: Source[];
  // Which source to show
  sourceIndex?: number;
  // Pass custom attributes to each image
  customAttributes?: Record<string, string>[];
};

// The type definitions do not support all props in the version we are using so extending this here.
interface ExtendedLightboxProps extends FsLightboxProps {
  customAttributes?: Record<string, string>[];
}

const ExtendedLighbox = ({ ...props }: ExtendedLightboxProps) => (
  <FsLightbox {...props} />
);

export const Lightbox = ({
  open,
  onClose,
  sources,
  sourceIndex,
  customAttributes,
}: LightboxProps) => {
  // Tracks the true open state of the modal by hooking into open/close events
  const trueOpenRef = useRef(false);
  // Interact with the default toggling behaviour of fslightbox-react
  const [toggler, toggle] = useState(open);

  // If the true open state does not match the intended open state giving by the user, we need to toggle the true state to match the intended state
  useEffect(() => {
    if (trueOpenRef.current !== open) {
      toggle((x) => !x);
    }
  }, [open]);

  // Parse the source strings to create custom sources if needed, eg for orbitvu
  const parsedSources = parseSources(sources) as any;
  const types = parsedSources.map(getType);

  return (
    <ExtendedLighbox
      types={types}
      sources={parsedSources}
      sourceIndex={sourceIndex}
      toggler={toggler}
      onOpen={() => {
        trueOpenRef.current = true;
      }}
      onClose={() => {
        trueOpenRef.current = false;
        onClose?.();
      }}
      customAttributes={customAttributes}
    />
  );
};

Lightbox.displayName = "Lightbox";

export default Lightbox;

/** Determines if a url is an orbitvu link */
export function isOrbitvu(src: string): boolean {
  return src.startsWith("https://orbitvu.co");
}

/** Determines if a url is a youtube link */
export function isYoutube(src: string): boolean {
  return src.startsWith("https://www.youtube.com");
}

// https://fslightbox.com/react/documentation/types
// We can use 'custom' when we have a custom source even though it is invalid because a custom source doesn't care about the type anyway
type AssetType = "image" | "video" | "youtube" | "custom";

// We only support orbitvu/custom, youtube and image
function getType(source: Source): AssetType {
  if (typeof source !== "string") {
    return "custom";
  }

  return isOrbitvu(source) ? "custom" : isYoutube(source) ? "youtube" : "image";
}

function parseSources(sources: Source[]) {
  return sources.map((source) => {
    if (typeof source !== "string") {
      return source;
    }

    if (isOrbitvu(source)) {
      return (
        <iframe
          title="Orbitvu 360 product image viewer"
          src={source}
          width="1920px"
          height="1080px"
          frameBorder="0"
        />
      );
    }

    return source;
  });
}
